/** QuickRouter — Nets : liste avec état de routage, mise en évidence, création à partir
 *  d'une liste de broches (« U1.3, R1.1, J1.GND »), classe, largeur, routage ciblé. */
import { useMemo, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { routingStatus } from '../../shared/netlist.js';
import * as ops from '../../shared/ops.js';
import NumField from './NumField.jsx';

export default function NetsPanel() {
  const project = useProjectStore((s) => s.project);
  const highlight = useProjectStore((s) => s.highlightNet);
  const setHighlight = useProjectStore((s) => s.setHighlightNet);
  const mutate = useProjectStore((s) => s.mutate);
  const autoroute = useProjectStore((s) => s.autoroute);
  const routing = useProjectStore((s) => s.routing);
  const readOnly = project.role === 'viewer';
  const [name, setName] = useState('');
  const [pins, setPins] = useState('');
  const [open, setOpen] = useState(null);
  const lookup = useProjectStore.getState().lookup();
  const status = useMemo(() => routingStatus(project, lookup), [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const byName = new Map(status.nets.map((n) => [n.name, n]));

  const create = () => {
    const list = pins.split(/[\s,;]+/).filter(Boolean);
    if (!name.trim() || !list.length) return;
    const ok = mutate((p, lk) => ops.setNet(p, lk, { name: name.trim(), pins: list, mode: 'add' }));
    if (ok) { setName(''); setPins(''); }
  };

  return (
    <div className="panel-body">
      <div className="muted">{status.routed}/{status.total} nets routés · {status.unrouted} liaison{status.unrouted > 1 ? 's' : ''} restante{status.unrouted > 1 ? 's' : ''}</div>
      <div className="nets">
        {project.nets.map((n) => {
          const st = byName.get(n.name);
          return (
            <div key={n.id} className={`net-row ${highlight === n.name ? 'selected' : ''}`}>
              <div className="row" onClick={() => setHighlight(highlight === n.name ? null : n.name)}>
                <span className={`dot ${st?.done ? 'ok' : 'todo'}`} title={st?.done ? 'routé' : `${st?.rats.length} liaison(s) à router`} />
                <span className="grow net-name">{n.name}</span>
                {n.kind === 'power' && <span className="tag">puiss.</span>}
                <span className="muted">{n.pins.length}</span>
                <button className="icon" title="Détails" onClick={(e) => { e.stopPropagation(); setOpen(open === n.id ? null : n.id); }}>{open === n.id ? '▾' : '▸'}</button>
              </div>
              {open === n.id && (
                <div className="net-detail">
                  <div className="mono small">{n.pins.join(' ')}</div>
                  <div className="row wrap">
                    <select disabled={readOnly} value={n.kind} onChange={(e) => mutate((p) => { p.nets.find((x) => x.id === n.id).kind = e.target.value; })}>
                      <option value="signal">Signal</option>
                      <option value="power">Puissance</option>
                    </select>
                    <label className="muted">Largeur</label>
                    <NumField style={{ width: 60 }} disabled={readOnly} value={n.width ?? null} allowNull placeholder="auto" onCommit={(v) => mutate((p) => { const x = p.nets.find((y) => y.id === n.id); if (v) x.width = v; else delete x.width; })} />
                  </div>
                  <div className="row wrap">
                    <button disabled={readOnly || routing} onClick={() => autoroute([n.name])}>Router ce net</button>
                    <button disabled={readOnly} onClick={() => mutate((p) => ops.clearRoutes(p, { nets: [n.name] }))}>Effacer ses pistes</button>
                    <button className="danger" disabled={readOnly} onClick={() => window.confirm(`Supprimer le net ${n.name} et ses pistes ?`) && mutate((p, lk) => ops.deleteNets(p, [n.name], { lookup: lk }))}>Supprimer</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!project.nets.length && <div className="empty">Aucun net. Reliez des broches ci-dessous, depuis les propriétés d'un composant, ou demandez à l'assistant de câbler le schéma.</div>}
      </div>
      {!readOnly && (
        <>
          <h3>Relier des broches</h3>
          <input placeholder="Nom du net (GND, +3V3, RS485_A…)" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
          <textarea rows={2} placeholder="Broches : U1.3, R1.1, J1.GND…" value={pins} onChange={(e) => setPins(e.target.value)} onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) create(); }} style={{ width: '100%', marginTop: 4 }} />
          <button className="primary" onClick={create} disabled={!name.trim() || !pins.trim()}>Ajouter au net</button>
        </>
      )}
    </div>
  );
}
